/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx, cssMap } from '@compiled/react';
import { useIntl } from 'react-intl';

import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages/ai-suggestions';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { token } from '@atlaskit/tokens';

/**
 * The default Atlassian Intelligence disclaimer link. This mirrors the link used by the
 * generative AI modal footer (`FooterDisclaimer`) and the suggestion card footer in
 * editor-plugin-ai-suggestions (`SuggestionCardFooter`).
 */
const DEFAULT_DISCLAIMER_URL = 'https://www.atlassian.com/trust/atlassian-intelligence';

const styles = cssMap({
	link: {
		font: token('font.body.small'),
		color: token('color.text.subtlest'),
		textDecoration: 'none',
		'&:visited': {
			color: token('color.text.subtlest'),
		},
		'&:hover': {
			color: token('color.text.subtlest'),
			textDecoration: 'none',
		},
	},
});

/**
 * AI policy footer for the suggestions side panel.
 *
 * Renders: [info icon] "Uses AI. Verify results." [link-out icon] [Labs lozenge]
 * where the link-out icon links to the Atlassian Intelligence disclaimer page.
 * Both the disclaimer and Labs lozenge are centred as a group.
 *
 * Copied from platform/packages/editor/editor-plugin-ai-suggestions/src/ui/components/SuggestionCard/SuggestionCardFooter.tsx
 */
export function AiPolicyFooter(): JSX.Element {
	const { formatMessage } = useIntl();

	return (
		<Inline space="space.200" alignBlock="center" alignInline="center">
			<Inline space="space.050" alignBlock="center">
				<Anchor
					href={DEFAULT_DISCLAIMER_URL}
					target="_blank"
					rel="noopener noreferrer"
					xcss={styles.link}
				>
					<Inline space="space.050" alignBlock="center" as="span">
						{formatMessage(aiSuggestionsMessages.cardDisclaimerText)}
					</Inline>
				</Anchor>
			</Inline>
			<Lozenge appearance="discovery">
				{formatMessage(aiSuggestionsMessages.labsLozengeLabel)}
			</Lozenge>
		</Inline>
	);
}
